import React, { useMemo, useState } from 'react';
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import { PERMISSIONS } from '../../constants/permissions';
import { usePermission } from '../../hooks/usePermission';
import { helpElement } from '../../help/attrs';
import { AuthService } from '../../services/auth.service';
import BonusRegisterTab from './BonusRegisterTab';
import OvertimeRegisterTab from './OvertimeRegisterTab';
import LeaveRegisterTab from './LeaveRegisterTab';
import ServiceRecordTab from './ServiceRecordTab';
import HourBreachReportTab from './HourBreachReportTab';
import WppfRegisterTab from './WppfRegisterTab';
import WageComplianceTab from './WageComplianceTab';
import InsuranceRegisterTab from './InsuranceRegisterTab';
import WageSheetTab from './WageSheetTab';
import AuditPackTab from './AuditPackTab';

/** Literal help keys per tab (the key-coverage scan reads literals only). */
const TAB_HELP: Record<string, { helpKey: string }> = {
    bonus: { helpKey: 'statutory.tab.bonus' },
    overtime: { helpKey: 'statutory.tab.overtime' },
    hourBreaches: { helpKey: 'statutory.tab.hourBreaches' },
    wppf: { helpKey: 'statutory.tab.wppf' },
    wageCompliance: { helpKey: 'statutory.tab.wageCompliance' },
    leave: { helpKey: 'statutory.tab.leave' },
    serviceRecord: { helpKey: 'statutory.tab.serviceRecord' },
    insurance: { helpKey: 'statutory.tab.insurance' },
    wageSheet: { helpKey: 'statutory.tab.wageSheet' },
    auditPack: { helpKey: 'statutory.tab.auditPack' },
};

type TabKey = 'bonus' | 'overtime' | 'leave' | 'serviceRecord' | 'hourBreaches' | 'wppf' | 'wageCompliance' | 'insurance' | 'wageSheet' | 'auditPack';

/**
 * Statutory registers bundle (spec §2, plus the hour-breach report,
 * {@code docs/decisions/2026-09-08-hour-breach-report.md} D10, the WPPF register,
 * {@code docs/decisions/2026-09-08-wppf-register.md} REV 2 D7/R14, the wage compliance register,
 * {@code docs/decisions/2026-09-08-wage-grades.md} REV 2 D3, and the group-insurance register,
 * {@code docs/decisions/2026-09-08-group-insurance.md} REV 2 D5/R6, and the audit-format wage
 * sheet + one-click audit pack, {@code docs/decisions/2026-09-11-garments-audit-wage-sheet-and-audit-pack.md}
 * §3(A)/§3(B)): one page, TEN tabs, FOUR permission hooks — bonus, overtime, hour-breaches, WPPF,
 * wage compliance, wage sheet, and audit pack all share PAYROLL_VIEW (H6/D7/D3); insurance is its
 * OWN key, INSURANCE_VIEW, deliberately NOT
 * PAYROLL_VIEW (R6/H2 — §99 applicability must stay reachable for a payroll-disabled tenant). The
 * route this page is mounted on carries NO permission guard of its own (see App.tsx) so that a
 * caller holding none of the four keys still lands here and sees the explanation below, rather
 * than a raw 403 or a blank page.
 */
const StatutoryRegistersPage: React.FC = () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    const canPayroll = usePermission(PERMISSIONS.PAYROLL_VIEW);
    const canLeave = usePermission(PERMISSIONS.LEAVE_BALANCE_VIEW_ALL);
    const canProfile = usePermission(PERMISSIONS.PROFILE_VIEW_ALL);
    const canInsurance = usePermission(PERMISSIONS.INSURANCE_VIEW);

    const tabs = useMemo(() => ([
        { key: 'bonus' as TabKey, label: 'Bonus', visible: canPayroll },
        { key: 'overtime' as TabKey, label: 'Overtime', visible: canPayroll },
        { key: 'hourBreaches' as TabKey, label: 'Hour Breaches', visible: canPayroll },
        { key: 'wppf' as TabKey, label: 'WPPF', visible: canPayroll },
        { key: 'wageCompliance' as TabKey, label: 'Wage Compliance', visible: canPayroll },
        { key: 'leave' as TabKey, label: 'Leave', visible: canLeave },
        { key: 'serviceRecord' as TabKey, label: 'Service Record', visible: canProfile },
        { key: 'insurance' as TabKey, label: 'Insurance', visible: canInsurance },
        { key: 'wageSheet' as TabKey, label: 'Wage Sheet', visible: canPayroll },
        { key: 'auditPack' as TabKey, label: 'Audit Pack', visible: canPayroll },
    ]), [canPayroll, canLeave, canProfile, canInsurance]);

    const visibleTabs = tabs.filter(t => t.visible);
    const [activeKey, setActiveKey] = useState<TabKey | null>(visibleTabs[0]?.key ?? null);

    const active = visibleTabs.find(t => t.key === activeKey) ?? visibleTabs[0] ?? null;

    if (visibleTabs.length === 0) {
        return (
            <Box p={3} data-help-page="statutory.page">
                <Typography variant="h5" mb={2}>Statutory Registers</Typography>
                <Paper sx={{ p: 3 }} data-testid="statutory-registers-empty-state" data-help="statutory.noAccess">
                    <Typography color="text.secondary">
                        Statutory registers require the Payroll, Leave and People modules
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box p={3} data-help-page="statutory.page">
            <Typography variant="h5" mb={2}>Statutory Registers</Typography>
            <Tabs
                data-help-section="statutory.tabs"
                value={active?.key ?? false}
                onChange={(_, key) => setActiveKey(key)}
                sx={{ mb: 2 }}
            >
                {visibleTabs.map(t => <Tab key={t.key} value={t.key} label={t.label} {...helpElement(TAB_HELP[t.key]?.helpKey)} />)}
            </Tabs>

            {active?.key === 'bonus' && <BonusRegisterTab institutionId={institutionId} />}
            {active?.key === 'overtime' && <OvertimeRegisterTab institutionId={institutionId} />}
            {active?.key === 'hourBreaches' && <HourBreachReportTab institutionId={institutionId} />}
            {active?.key === 'wppf' && <WppfRegisterTab institutionId={institutionId} />}
            {active?.key === 'wageCompliance' && <WageComplianceTab institutionId={institutionId} />}
            {active?.key === 'leave' && <LeaveRegisterTab institutionId={institutionId} />}
            {active?.key === 'serviceRecord' && <ServiceRecordTab institutionId={institutionId} />}
            {active?.key === 'insurance' && <InsuranceRegisterTab institutionId={institutionId} />}
            {active?.key === 'wageSheet' && <WageSheetTab institutionId={institutionId} />}
            {active?.key === 'auditPack' && <AuditPackTab institutionId={institutionId} />}
        </Box>
    );
};

export default StatutoryRegistersPage;
